import type { AcceptedPrice, StartQuote, StartRecordsResponse } from '@aica/api-types';
import { startPriceMismatchSchema } from '@aica/api-types/schemas';
import type { StartRecordsOptions } from './types';

/**
 * Цена ручного запуска на стороне клиента (#1396) — одна проводка на оба
 * состояния запуска (портал и Bitrix24). Деньги в двух копиях разошлись бы на
 * первой же правке.
 */

/**
 * Котировка, показанная в диалоге, — то, на что человек согласился: цена и
 * за что она названа. Сервер откажет, если по набору, который он запустит,
 * выросла цена, число записей или число звонков без длительности.
 */
export function acceptedPriceOf(quote: {
  priceUsd: number;
  currency: string;
  records: number;
  unknownDurationCalls?: number | null;
}): AcceptedPrice {
  return {
    priceUsd: quote.priceUsd,
    currency: quote.currency,
    records: quote.records,
    unknownDurationCalls: quote.unknownDurationCalls ?? 0,
  };
}

/**
 * Свежая котировка из отказа «цена выросла с момента показа» — или null, если
 * ошибка другая.
 *
 * Тело ответа лежит в `ApiError.data` (`fetch-utils.ts`). Проверяется схемой,
 * а не приводится к типу: цена, которую интерфейс покажет человеку, не может
 * прийти «как есть» из чего угодно, похожего на ответ.
 */
export function priceChangedQuote(error: unknown): StartQuote | null {
  const data =
    typeof error === 'object' && error !== null && 'data' in error
      ? (error as { data?: unknown }).data
      : undefined;
  const parsed = startPriceMismatchSchema.safeParse(data);
  return parsed.success ? parsed.data.quote : null;
}

/**
 * Обработчики одного вызова запуска: успех и отказ «цена выросла» уходят
 * вызывающему. Успех — только по факту: выбор снимается, когда запуск
 * состоялся, а не когда его отправили, — иначе отказ по цене открывал бы
 * диалог для записей, которые уже не выбраны.
 */
export function quotedStartCallbacks(options?: StartRecordsOptions) {
  return {
    onSuccess: (result: StartRecordsResponse) => options?.onStarted?.(result),
    onError: (error: unknown) => {
      const quote = priceChangedQuote(error);
      if (quote) options?.onPriceChanged?.(quote);
    },
  };
}
